/** Seat-based license program types the frontend knows how to display. */
export enum LICENSE_BASED_PROGRAM_TYPES {
  MANAGER_COACHING = 'manager_coaching',
}
export const MANAGER_COACHING_PROGRAM_TYPE = LICENSE_BASED_PROGRAM_TYPES.MANAGER_COACHING

export const isLicenseBasedProgramType = (programType: string): programType is LICENSE_BASED_PROGRAM_TYPES =>
  (Object.values(LICENSE_BASED_PROGRAM_TYPES) as string[]).includes(programType)

export interface SeatBasedLicense {
  eligible: boolean
  program_type: string
  license_id: string
  access_start_date: string
  access_end_date: string
  sessions_total: number
  used_session_count?: number | null
}

export interface SeatBasedLicensesResponse {
  licenses: SeatBasedLicense[]
}

export enum MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS {
  LOADING = 'loading',
  REQUEST_FAILED = 'requestFailed',
  MISSING_LICENSE = 'missingLicense',
  INELIGIBLE = 'ineligible',
  ELIGIBLE = 'eligible',
}

export interface ManagerCoachingSeatEligibility {
  status: MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS
  license?: SeatBasedLicense
}

export interface ShouldFetchManagerCoachingSeatEligibilityParams {
  isUserLoggedIn: boolean
  lyraId?: string
  isManagerHubEligible: boolean
  hasActiveManagerHubPromotionSignal: boolean
  fetchSeatEligibilityForManagerHubPage: boolean
}

export const shouldFetchManagerCoachingSeatEligibility = ({
  isUserLoggedIn,
  lyraId,
  isManagerHubEligible,
  hasActiveManagerHubPromotionSignal,
  fetchSeatEligibilityForManagerHubPage,
}: ShouldFetchManagerCoachingSeatEligibilityParams): boolean =>
  isUserLoggedIn &&
  Boolean(lyraId) &&
  isManagerHubEligible &&
  (hasActiveManagerHubPromotionSignal || fetchSeatEligibilityForManagerHubPage)

/**
 * Maps seat-based-licenses request state (e.g. from an RTK Query hook) to the shared Manager
 * Coaching seat eligibility. The selected license's `eligible` boolean is the only eligibility
 * value; access dates and session counts must not be used to derive it. Consumers get distinct
 * LOADING / REQUEST_FAILED / MISSING_LICENSE / INELIGIBLE / ELIGIBLE states so each can apply
 * its documented fallback. An unstarted or skipped request reports LOADING so seat state reads
 * as unknown.
 */
export const getManagerCoachingSeatEligibility = ({
  isLoading,
  isError,
  data,
}: {
  isLoading: boolean
  isError: boolean
  data?: SeatBasedLicensesResponse
}): ManagerCoachingSeatEligibility => {
  if (isError) {
    return { status: MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.REQUEST_FAILED }
  }
  if (isLoading || !data) {
    return { status: MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.LOADING }
  }
  const license = data.licenses?.find(({ program_type }) => program_type === MANAGER_COACHING_PROGRAM_TYPE)
  if (!license) {
    return { status: MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.MISSING_LICENSE }
  }
  return {
    status: license.eligible
      ? MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.ELIGIBLE
      : MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.INELIGIBLE,
    license,
  }
}

/**
 * An eligible seat holder keeps the default-ranked card, and an unknown seat state
 * (loading or request failure) must not surface the proactive generic card, so triggered
 * promotion is allowed only for a resolved missing or ineligible license.
 */
export const canPromoteManagerHubBasedOnSeatEligibility = (seatEligibility?: ManagerCoachingSeatEligibility): boolean =>
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.MISSING_LICENSE ||
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.INELIGIBLE

/**
 * Returns the Manager Coaching license only when the seat is currently eligible. Ineligible seats
 * (revoked or expired) still carry a `license` on the eligibility object for diagnostics, but must not
 * surface license-based care options or session counts.
 */
export const getEligibleManagerCoachingLicense = (
  seatEligibility?: ManagerCoachingSeatEligibility,
): SeatBasedLicense | undefined =>
  seatEligibility?.status === MANAGER_COACHING_SEAT_ELIGIBILITY_STATUS.ELIGIBLE ? seatEligibility.license : undefined
